import {computed, type Ref} from "vue";
import {useQuery, useQueries, useMutation, useQueryClient, keepPreviousData, type QueryClient} from "@tanstack/vue-query";
import {fetchWrapper} from "@/helpers/fetch-wrapper";
import type {ApiError} from "@/helpers/api-error";
import type {Project, Product, ProjectWorkflowView, RunHistory} from "@/types";

/** Mirrors BlackBox.Application.Manage.PagedResult<T> — one page plus the unpaged total. */
export interface Paged<T> {
    items: T[];
    totalCount: number;
    skip: number;
    take: number;
}
import {
    serverFilterParamsToQueryString,
    type ServerFilterParams,
} from "@/views/builder/composables/useHistoryFilters";

const PRODUCTS_BASE = `${import.meta.env.VITE_API_URL}/products`;

const BASE = `${import.meta.env.VITE_API_URL}/projects`;

// Refresh every query that carries a project's run-readiness (isReady) flag, so the "Setup Required"
// badge — on the detail view, the projects list, and the dashboard — clears/updates immediately after
// a mutation that changes readiness (e.g. binding a connection slot). Without this the badge is stale
// until a manual page reload.
function invalidateReadinessQueries(queryClient: QueryClient, projectId: string) {
    queryClient.invalidateQueries({ queryKey: ["project", projectId] }); // detail badge (GetProjectById.isReady)
    queryClient.invalidateQueries({ queryKey: ["projects"] });            // list badge
    queryClient.invalidateQueries({ queryKey: ["projects-readiness"] });  // dashboard badges
}

// READ — list
export function useProjects() {
    const {data, isFetching: projectsLoading, error: projectsError, refetch: refetchProjects} = useQuery({
        queryKey: ["projects"],
        queryFn: () => fetchWrapper.get<Project[]>(BASE),
    });
    const projects = computed(() => data.value ?? []);

    return {projects, projectsLoading, projectsError: projectsError as Ref<ApiError | null>, refetchProjects};
}

// READ — per-project run-readiness status (wizard + connections + datastores).
// Split from useProjects so the dashboard list paints immediately and badges fill in when this
// resolves. Returns a Map keyed by projectId so the dashboard can look up O(1) per row.
export interface ProjectReadinessStatusEntry {
    projectId: string;
    isReady: boolean;
}

export function useProjectsReadiness() {
    const {data, isFetching, error} = useQuery({
        queryKey: ["projects-readiness"],
        queryFn: () => fetchWrapper.get<ProjectReadinessStatusEntry[]>(`${BASE}/readiness-status`),
    });
    const readinessByProject = computed(() => {
        const map = new Map<string, boolean>();
        for (const entry of data.value ?? []) map.set(entry.projectId, entry.isReady);
        return map;
    });
    return {
        readinessByProject,
        readinessLoading: isFetching,
        readinessError: error as Ref<ApiError | null>,
    };
}

// READ — per-project live workflow count ("unique workflows this project can run").
// Split from useProjects for the same reason readiness-status was: computing the count runs a
// query per project, and bolting it onto the list endpoint held up first paint. Fired in
// parallel from the dashboard; the list paints immediately and the workflow-count cell on each
// card fills in when this resolves. Bypasses the broken `project.workflowCount` field on the
// list payload (the backend's denormalised counter is only maintained for master-authored
// workflows and reports 0 for subscriber forks).
export interface ProjectWorkflowCountEntry {
    projectId: string;
    count: number;
}

// READ — per-project most-recent run timestamp ("Last run" on the dashboard cards). Split from
// useProjects for the same reason as the counts above: the list paints immediately and each card's
// last-run fills in when this resolves. Keyed by projectId; projects that have never executed a
// workflow are absent from the map (rendered as "Never"). Attributed by RunningProjectId, so
// authoring (PublisherMaster) projects won't appear — they don't execute workflows.
export interface ProjectLastRunEntry {
    projectId: string;
    lastRun: string;
}

export function useProjectsLastRuns() {
    const {data, isFetching, error} = useQuery({
        queryKey: ["projects-last-runs"],
        queryFn: () => fetchWrapper.get<ProjectLastRunEntry[]>(`${BASE}/last-runs`),
    });
    const lastRunByProject = computed(() => {
        const map = new Map<string, string>();
        for (const entry of data.value ?? []) map.set(entry.projectId, entry.lastRun);
        return map;
    });
    return {
        lastRunByProject,
        lastRunsLoading: isFetching,
        lastRunsError: error as Ref<ApiError | null>,
    };
}

export function useProjectsWorkflowCounts() {
    const {data, isFetching, error} = useQuery({
        queryKey: ["projects-workflow-counts"],
        queryFn: () => fetchWrapper.get<ProjectWorkflowCountEntry[]>(`${BASE}/workflow-counts`),
    });
    const workflowCountsByProject = computed(() => {
        const map = new Map<string, number>();
        for (const entry of data.value ?? []) map.set(entry.projectId, entry.count);
        return map;
    });
    return {
        workflowCountsByProject,
        workflowCountsLoading: isFetching,
        workflowCountsError: error as Ref<ApiError | null>,
    };
}

// READ — single (edit screen — does not trigger list fetch)
export function useProject(id: Ref<string>) {
    const {data: project, isFetching: projectLoading, error: projectError} = useQuery({
        queryKey: ["project", id],
        queryFn: () => fetchWrapper.get<Project>(`${BASE}/${id.value}`),
        enabled: computed(() => !!id.value),
    });

    return {project, projectLoading, projectError: projectError as Ref<ApiError | null>};
}

// READ — workflows for a project (lazy — only fetches when enabled)
export function useProjectWorkflows(id: Ref<string>, enabled: Ref<boolean>) {
    const {data, isFetching: projectWorkflowsLoading, error: projectWorkflowsError, refetch: refetchProjectWorkflows} = useQuery({
        queryKey: computed(() => ["project-workflows", id.value]),
        queryFn: () => fetchWrapper.get<ProjectWorkflowView[]>(`${BASE}/${id.value}/workflows`),
        enabled: computed(() => enabled.value && !!id.value),
    });
    const projectWorkflows = computed(() => data.value ?? []);

    return {
        projectWorkflows,
        projectWorkflowsLoading,
        projectWorkflowsError: projectWorkflowsError as Ref<ApiError | null>,
        refetchProjectWorkflows
    };
}

// READ — one offset page of a project's run history (ADR-007). Optional filterParams ref drives
// server-side filtering, sorting, and paging (skip/take/sortBy/workflowId). When its value changes
// the query refetches because the serialised query string is part of the queryKey. keepPreviousData
// holds the current page on screen while the next loads, so paging doesn't blank the list.
// projectRunsLoading is the *initial* load only; projectRunsFetching covers background refetches.
export function useProjectRuns(id: Ref<string>, filterParams?: Ref<ServerFilterParams>) {
    const queryString = computed(() =>
        filterParams ? serverFilterParamsToQueryString(filterParams.value) : ""
    );
    const {data, isLoading: projectRunsLoading, isFetching: projectRunsFetching, error: projectRunsError} = useQuery({
        queryKey: computed(() => ["project-runs", id.value, queryString.value]),
        queryFn: () => {
            const qs = queryString.value ? `?${queryString.value}` : "";
            return fetchWrapper.get<Paged<RunHistory>>(`${BASE}/${id.value}/runs${qs}`);
        },
        enabled: computed(() => !!id.value),
        placeholderData: keepPreviousData,
    });
    const projectRuns = computed(() => data.value?.items ?? []);
    const projectRunsTotal = computed(() => data.value?.totalCount ?? 0);

    return {
        projectRuns,
        projectRunsTotal,
        projectRunsLoading,
        projectRunsFetching,
        projectRunsError: projectRunsError as Ref<ApiError | null>,
    };
}

// READ — aggregate run-history stats for the project (Total / Succeeded / Avg duration).
// Backed by a dedicated endpoint that aggregates server-side, so we don't pull the full run
// list to the client just to count rows.
export interface ProjectRunStats {
    totalRuns: number;
    succeededCount: number;
    failedCount: number;
    warningCount: number;
    pendingCount: number;
    avgDurationMs: number;
}

export function useProjectRunStats(id: Ref<string>) {
    const {data, isFetching: projectRunStatsLoading, error: projectRunStatsError} = useQuery({
        queryKey: computed(() => ["project-run-stats", id.value]),
        queryFn: () => fetchWrapper.get<ProjectRunStats>(`${BASE}/${id.value}/run-stats`),
        enabled: computed(() => !!id.value),
    });
    return {
        projectRunStats: data,
        projectRunStatsLoading,
        projectRunStatsError: projectRunStatsError as Ref<ApiError | null>,
    };
}

// WRITE — create
// Shape mirrors the backend's CreateProjectRequest: Name, ProjectType, optional ProductId.
// PublisherMaster projects aren't created here — they originate from the product-creation flow.
export interface CreateProjectPayload {
    name: string;
    projectType: "DIY" | "Subscriber";
    productId?: string;
}

export function useCreateProject() {
    const queryClient = useQueryClient();

    const {mutate, mutateAsync, isPending, error, reset} = useMutation<
        string,
        ApiError,
        CreateProjectPayload
    >({
        mutationFn: (payload: CreateProjectPayload) => fetchWrapper.post<string>(BASE, payload),
        onSuccess: () => queryClient.invalidateQueries({queryKey: ["projects"]}),
    });

    return {
        createProject: mutate,
        createProjectAsync: mutateAsync,
        createProjectPending: isPending,
        createProjectError: error as Ref<ApiError | null>,
        createProjectReset: reset,
    };
}

// WRITE — update / save
export function useUpdateProject() {
    const queryClient = useQueryClient();

    const {mutate, mutateAsync, isPending, error, reset} = useMutation<
        Project,
        ApiError,
        Partial<Project> & { id: string }
    >({
        mutationFn: ({id, ...payload}: Partial<Project> & { id: string }) =>
            fetchWrapper.put<Project>(`${BASE}/${id}`, payload),
        onSuccess: (_data, {id}) => {
            queryClient.invalidateQueries({queryKey: ["projects"]});
            queryClient.invalidateQueries({queryKey: ["project", id]});
        },
    });

    return {
        updateProject: mutate,
        updateProjectAsync: mutateAsync,
        updateProjectPending: isPending,
        updateProjectError: error as Ref<ApiError | null>,
        updateProjectReset: reset,
    };
}

// READ — wizard sequence for a publisher project
export function useWizardSequence(projectId: Ref<string>) {
    const {data, isFetching, error} = useQuery({
        queryKey: computed(() => ["wizard-sequence", projectId.value]),
        queryFn: () => fetchWrapper.get<string[]>(`${BASE}/${projectId.value}/wizard-sequence`),
        enabled: computed(() => !!projectId.value),
    });
    const wizardSequence = computed(() => data.value ?? []);
    return {wizardSequence, wizardSequenceLoading: isFetching, wizardSequenceError: error as Ref<ApiError | null>};
}

// READ — wizard steps for a subscriber project (resolves workflow versions, includes deprecated badge)
export interface ProjectWizardStepView {
    id: string;
    name: string;
    componentType: string;
    isRequired: boolean;
    order: number;
    properties: Record<string, unknown>;
}

export interface ProjectWizardDefinitionView {
    id: string;
    name: string;
    description: string;
    isActive: boolean;
    steps: ProjectWizardStepView[];
}

/**
 * Derived wizard completion for a workflow. The backend computes this — a workflow is complete
 * when every required step has a non-empty saved value. Not stored anywhere.
 */
export interface WorkflowWizardCompletionView {
    isComplete: boolean;
    requiredStepCount: number;
    completedRequiredStepCount: number;
    missingSteps: { stepId: string; stepName: string }[];
}

export interface WizardCompletionSummaryView {
    isComplete: boolean;
    completedWorkflowCount: number;
    totalWorkflowCount: number;
}

export interface ProjectWizardEntryView {
    workflowId: string;
    workflowVersionId: string;
    workflowName: string;
    versionLabel: string;
    isDeprecatedByPublisher: boolean;
    isCustomized: boolean;
    wizardDefinitions: ProjectWizardDefinitionView[];
    savedConfiguration: Record<string, unknown>;
    completion: WorkflowWizardCompletionView;
    /**
     * Subset of `completion.missingSteps` that became required since the subscriber's previous
     * product version. Drives the "added by recent product update" banner inside the wizard.
     * Empty for fresh subscribers, publisher master views, and projects whose current gaps were
     * already required before the upgrade.
     */
    newlyRequiredMissingSteps: { stepId: string; stepName: string }[];
}

export interface ProjectWizardSequenceView {
    projectId: string;
    entries: ProjectWizardEntryView[];
    completionSummary: WizardCompletionSummaryView;
}

export function useProjectWizardSteps(projectId: Ref<string>, enabled: Ref<boolean>) {
    const {data, isFetching, error, refetch} = useQuery({
        queryKey: computed(() => ["project-wizard-steps", projectId.value]),
        queryFn: () => fetchWrapper.get<ProjectWizardSequenceView>(`${BASE}/${projectId.value}/wizard-steps`),
        enabled: computed(() => enabled.value && !!projectId.value),
    });
    const wizardSteps = computed(() => data.value ?? null);
    return {
        wizardSteps,
        wizardStepsLoading: isFetching,
        wizardStepsError: error as Ref<ApiError | null>,
        refetchWizardSteps: refetch,
    };
}

// WRITE — save the user's wizard answers for a single workflow within a subscriber project.
// Configuration is the merged form data for all vueform steps in that workflow.
export function useSaveWizardConfiguration(projectId: Ref<string>) {
    const queryClient = useQueryClient();
    const {mutate, mutateAsync, isPending, error, reset} = useMutation<
        unknown,
        ApiError,
        { workflowId: string; configuration: Record<string, unknown> }
    >({
        mutationFn: ({workflowId, configuration}) =>
            fetchWrapper.put(
                `${BASE}/${projectId.value}/workflows/${workflowId}/wizard-configuration`,
                configuration
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["project-wizard-steps", projectId.value]});
            // Answering a required wizard step changes readiness — refresh the badge queries too.
            invalidateReadinessQueries(queryClient, projectId.value);
        },
    });
    return {
        saveWizardConfiguration: mutate,
        saveWizardConfigurationAsync: mutateAsync,
        saveWizardConfigurationPending: isPending,
        saveWizardConfigurationError: error as Ref<ApiError | null>,
        saveWizardConfigurationReset: reset,
    };
}

// Phase 5 — project export / import.
export interface ConnectionBindingExportView {
    slotName: string;
    connectionId: string;
    connectionName: string | null;
}

export interface ProjectExportBundleView {
    schemaVersion: string;
    projectName: string;
    projectType: string;
    productId: string | null;
    productVersionNumber: string | null;
    wizardConfigurations: Record<string, Record<string, unknown>>;
    connectionBindings: ConnectionBindingExportView[];
    datastores: Record<string, unknown>;
    exportedAt: string;
}

export interface ImportProjectResponse {
    projectId: string;
    unboundSlots: string[];
}

export function useExportProject(projectId: Ref<string>) {
    const { mutate, mutateAsync, isPending, error, reset } = useMutation<ProjectExportBundleView, ApiError, void>({
        mutationFn: () => fetchWrapper.get<ProjectExportBundleView>(`${BASE}/${projectId.value}/export`),
    });
    return {
        exportProject: mutate,
        exportProjectAsync: mutateAsync,
        exportProjectPending: isPending,
        exportProjectError: error as Ref<ApiError | null>,
        exportProjectReset: reset,
    };
}

export function useImportProject() {
    const queryClient = useQueryClient();
    const { mutate, mutateAsync, isPending, error, reset } = useMutation<
        ImportProjectResponse,
        ApiError,
        { bundle: ProjectExportBundleView; projectName?: string }
    >({
        // Target product is derived from the bundle on the backend — re-targeting isn't meaningful.
        // projectName overrides the bundle's name (defaults to bundle.projectName when omitted).
        mutationFn: ({ bundle, projectName }) =>
            fetchWrapper.post<ImportProjectResponse>(`${BASE}/import`, { bundle, projectName }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["projects"] });
        },
    });
    return {
        importProject: mutate,
        importProjectAsync: mutateAsync,
        importProjectPending: isPending,
        importProjectError: error as Ref<ApiError | null>,
        importProjectReset: reset,
    };
}

// Phase 3 — project-level datastores.
export interface ProjectDatastoreEntryView {
    name: string;
    description: string;
    value: unknown;
}

export interface ProjectDatastoresView {
    projectId: string;
    datastores: ProjectDatastoreEntryView[];
}

export function useProjectDatastores(projectId: Ref<string>, enabled: Ref<boolean>) {
    const { data: datastores, isPending: datastoresLoading, error: datastoresError, refetch } = useQuery({
        queryKey: computed(() => ["project-datastores", projectId.value]),
        queryFn: () => fetchWrapper.get<ProjectDatastoresView>(`${BASE}/${projectId.value}/datastores`),
        enabled: computed(() => enabled.value && !!projectId.value),
    });
    return {
        datastores,
        datastoresLoading,
        datastoresError: datastoresError as Ref<ApiError | null>,
        refetchDatastores: refetch,
    };
}

export function useSetProjectDatastoreValue(projectId: Ref<string>) {
    const queryClient = useQueryClient();
    const { mutate, mutateAsync, isPending, error, reset } = useMutation<
        unknown,
        ApiError,
        { name: string; value: unknown }
    >({
        mutationFn: ({ name, value }) =>
            fetchWrapper.put(
                `${BASE}/${projectId.value}/datastores/${encodeURIComponent(name)}`,
                { value }
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["project-datastores", projectId.value] });
        },
    });
    return {
        setDatastoreValue: mutate,
        setDatastoreValueAsync: mutateAsync,
        setDatastoreValuePending: isPending,
        setDatastoreValueError: error as Ref<ApiError | null>,
        setDatastoreValueReset: reset,
    };
}

// Phase 2 — project-level connection slot bindings.
export interface ProjectConnectionSlotEntry {
    slotName: string;
    connectionTemplateId: string;
    connectionTemplateName: string | null;
    boundConnectionId: string | null;
    boundConnectionName: string | null;
}

export interface ProjectConnectionBindingsView {
    projectId: string;
    slots: ProjectConnectionSlotEntry[];
}

export function useProjectConnectionBindings(projectId: Ref<string>, enabled: Ref<boolean>) {
    const { data: bindings, isPending: bindingsLoading, error: bindingsError, refetch } = useQuery({
        queryKey: computed(() => ["project-connection-bindings", projectId.value]),
        queryFn: () => fetchWrapper.get<ProjectConnectionBindingsView>(`${BASE}/${projectId.value}/connection-bindings`),
        enabled: computed(() => enabled.value && !!projectId.value),
    });
    return {
        bindings,
        bindingsLoading,
        bindingsError: bindingsError as Ref<ApiError | null>,
        refetchBindings: refetch,
    };
}

export function useBindConnection(projectId: Ref<string>) {
    const queryClient = useQueryClient();
    const { mutate, mutateAsync, isPending, error, reset } = useMutation<
        unknown,
        ApiError,
        { slotName: string; connectionTemplateId: string; connectionInstanceId: string }
    >({
        // A slot is identified by (name, templateId) under model D — both travel with the bind so the
        // backend matches the right declared slot and rejects a connection of the wrong template.
        mutationFn: ({ slotName, connectionTemplateId, connectionInstanceId }) =>
            fetchWrapper.put(
                `${BASE}/${projectId.value}/connection-bindings/${encodeURIComponent(slotName)}`,
                { connectionTemplateId, connectionInstanceId }
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["project-connection-bindings", projectId.value] });
            // Binding a slot changes run-readiness — refresh the queries that carry isReady so the
            // "Setup Required" badge clears without a manual page reload.
            invalidateReadinessQueries(queryClient, projectId.value);
        },
    });
    return {
        bindConnection: mutate,
        bindConnectionAsync: mutateAsync,
        bindConnectionPending: isPending,
        bindConnectionError: error as Ref<ApiError | null>,
        bindConnectionReset: reset,
    };
}

export function useUnbindConnection(projectId: Ref<string>) {
    const queryClient = useQueryClient();
    const { mutate, mutateAsync, isPending, error, reset } = useMutation<
        unknown,
        ApiError,
        { slotName: string; connectionTemplateId: string }
    >({
        mutationFn: ({ slotName, connectionTemplateId }) =>
            fetchWrapper.delete(
                `${BASE}/${projectId.value}/connection-bindings/${encodeURIComponent(slotName)}` +
                `?connectionTemplateId=${encodeURIComponent(connectionTemplateId)}`
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["project-connection-bindings", projectId.value] });
            // Unbinding can (re)introduce a readiness gap — same refresh as bind so the badge tracks it.
            invalidateReadinessQueries(queryClient, projectId.value);
        },
    });
    return {
        unbindConnection: mutate,
        unbindConnectionAsync: mutateAsync,
        unbindConnectionPending: isPending,
        unbindConnectionError: error as Ref<ApiError | null>,
        unbindConnectionReset: reset,
    };
}

// WRITE — fork a master workflow into a subscriber-owned customizable copy.
// Idempotent: re-calling on an already-customized workflow returns the existing fork's ID.
export interface CustomizedWorkflowResponse {
    forkedWorkflowId: string;
    forkedWorkflowVersionId: string;
}

export function useCustomizeWorkflow(projectId: Ref<string>) {
    const queryClient = useQueryClient();
    const {mutate, mutateAsync, isPending, error, reset} = useMutation<CustomizedWorkflowResponse, ApiError, string>({
        mutationFn: (originalWorkflowId) =>
            fetchWrapper.post<CustomizedWorkflowResponse>(
                `${BASE}/${projectId.value}/workflows/${originalWorkflowId}/customize`,
                {}
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["project-workflows", projectId.value]});
            queryClient.invalidateQueries({queryKey: ["project-wizard-steps", projectId.value]});
            queryClient.invalidateQueries({queryKey: ["project-version-diff", projectId.value]});
        },
    });
    return {
        customizeWorkflow: mutate,
        customizeWorkflowAsync: mutateAsync,
        customizeWorkflowPending: isPending,
        customizeWorkflowError: error as Ref<ApiError | null>,
        customizeWorkflowReset: reset,
    };
}

// WRITE — switch a workflow on/off for a project (operational gate; blocked at run-start when off).
export function useSetWorkflowActive(projectId: Ref<string>) {
    const queryClient = useQueryClient();
    const {mutate, mutateAsync, isPending, error, reset} = useMutation<
        unknown,
        ApiError,
        { workflowId: string; isActive: boolean }
    >({
        mutationFn: ({workflowId, isActive}) =>
            fetchWrapper.put(
                `${BASE}/${projectId.value}/workflows/${workflowId}/active`,
                { isActive }
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["project-workflows", projectId.value]});
        },
    });
    return {
        setWorkflowActive: mutate,
        setWorkflowActiveAsync: mutateAsync,
        setWorkflowActivePending: isPending,
        setWorkflowActiveError: error as Ref<ApiError | null>,
        setWorkflowActiveReset: reset,
    };
}

// WRITE — hard-delete a workflow. Calls the workflows endpoint but invalidates this project's workflow
// list. Rejects with an ApiError carrying `userMessage` on 409 when the backend blocks deletion
// (in-flight runs, active schedules, a shipped product version, or a same-project sub-workflow caller).
export function useDeleteWorkflow(projectId: Ref<string>) {
    const queryClient = useQueryClient();
    const {mutate, mutateAsync, isPending, error, reset} = useMutation<unknown, ApiError, string>({
        mutationFn: (workflowId: string) =>
            fetchWrapper.delete(`${import.meta.env.VITE_API_URL}/workflows/${workflowId}`),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["project-workflows", projectId.value]});
        },
    });
    return {
        deleteWorkflow: mutate,
        deleteWorkflowAsync: mutateAsync,
        deleteWorkflowPending: isPending,
        deleteWorkflowError: error as Ref<ApiError | null>,
        deleteWorkflowReset: reset,
    };
}

// READ — diff between subscriber project's current state and a target product version
export type WorkflowDiffStatus = "Unchanged" | "Changed" | "Added" | "Removed";

export type WizardUpgradePolicy = "Preserve" | "Overwrite";

export interface WizardFieldOverwriteView {
    stepId: string;
    stepName: string;
    fieldName: string;
    policy: WizardUpgradePolicy;
    currentValue: unknown;
    newValue: unknown;
}

export interface WorkflowDiffEntryView {
    workflowId: string;
    workflowName: string;
    status: WorkflowDiffStatus;
    currentVersionId: string | null;
    currentVersionLabel: string | null;
    targetVersionId: string | null;
    targetVersionLabel: string | null;
    isCurrentlyCustomized: boolean;
    customizationLostOnAccept: boolean;
    wizardFieldOverwrites: WizardFieldOverwriteView[];
}

export interface ProductVersionDiffView {
    projectId: string;
    currentProductVersionId: string;
    targetProductVersionId: string;
    targetVersionNumber: string;
    isUpgradeAvailable: boolean;
    customizationsLostOnAccept: number;
    workflows: WorkflowDiffEntryView[];
}

export function useProjectVersionDiff(projectId: Ref<string>, enabled: Ref<boolean>) {
    const {data, isFetching, error, refetch} = useQuery({
        queryKey: computed(() => ["project-version-diff", projectId.value]),
        queryFn: () => fetchWrapper.get<ProductVersionDiffView>(`${BASE}/${projectId.value}/version-diff`),
        enabled: computed(() => enabled.value && !!projectId.value),
    });
    return {
        versionDiff: computed(() => data.value ?? null),
        versionDiffLoading: isFetching,
        versionDiffError: error as Ref<ApiError | null>,
        refetchVersionDiff: refetch,
    };
}

// READ — version-diff fetched in parallel for many projects. Backed by `useQueries` so each
// project gets its own cache entry under the same `["project-version-diff", projectId]` key the
// single-project hook uses — switching between this batch view and the per-project upgrade banner
// re-uses cached data instead of re-fetching. The returned Map is keyed by projectId; entries are
// `null` while a query is still in flight or has errored.
export function useProjectsVersionDiffs(projectIds: Ref<string[]>) {
    const results = useQueries({
        queries: computed(() => projectIds.value.map((projectId) => ({
            queryKey: ["project-version-diff", projectId] as const,
            queryFn: () => fetchWrapper.get<ProductVersionDiffView>(`${BASE}/${projectId}/version-diff`),
            enabled: !!projectId,
        }))),
    });

    const diffsByProject = computed(() => {
        const map = new Map<string, ProductVersionDiffView | null>();
        const ids = projectIds.value;
        const arr = results.value as ReadonlyArray<{ data?: ProductVersionDiffView }>;
        for (let i = 0; i < ids.length; i++) {
            map.set(ids[i], arr[i]?.data ?? null);
        }
        return map;
    });

    const diffsLoading = computed(() =>
        (results.value as ReadonlyArray<{ isFetching: boolean }>).some(r => r.isFetching)
    );

    return { diffsByProject, diffsLoading };
}

// WRITE — accept product version upgrade (clears all pins, snaps to target version)
export function useAcceptProductVersionUpgrade(projectId: Ref<string>) {
    const queryClient = useQueryClient();
    const {mutate, mutateAsync, isPending, error, reset} = useMutation<unknown, ApiError, string>({
        mutationFn: (targetProductVersionId) =>
            fetchWrapper.post(`${BASE}/${projectId.value}/accept-upgrade`, { targetProductVersionId }),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["project", projectId.value]});
            queryClient.invalidateQueries({queryKey: ["project-version-diff", projectId.value]});
            queryClient.invalidateQueries({queryKey: ["project-wizard-steps", projectId.value]});
            queryClient.invalidateQueries({queryKey: ["project-workflows", projectId.value]});
        },
    });
    return {
        acceptUpgrade: mutate,
        acceptUpgradeAsync: mutateAsync,
        acceptUpgradePending: isPending,
        acceptUpgradeError: error as Ref<ApiError | null>,
        acceptUpgradeReset: reset,
    };
}

// WRITE — accept the publisher's update for a single customized workflow (clears just that pin).
export function useAcceptWorkflowUpgrade(projectId: Ref<string>) {
    const queryClient = useQueryClient();
    const {mutate, mutateAsync, isPending, error, reset} = useMutation<unknown, ApiError, string>({
        mutationFn: (workflowId) =>
            fetchWrapper.post(
                `${BASE}/${projectId.value}/workflows/${workflowId}/accept-upgrade`,
                {}
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["project", projectId.value]});
            queryClient.invalidateQueries({queryKey: ["project-version-diff", projectId.value]});
            queryClient.invalidateQueries({queryKey: ["project-wizard-steps", projectId.value]});
            queryClient.invalidateQueries({queryKey: ["project-workflows", projectId.value]});
        },
    });
    return {
        acceptWorkflowUpgrade: mutate,
        acceptWorkflowUpgradeAsync: mutateAsync,
        acceptWorkflowUpgradePending: isPending,
        acceptWorkflowUpgradeError: error as Ref<ApiError | null>,
        acceptWorkflowUpgradeReset: reset,
    };
}

// WRITE — remove a workflow's customization on demand and revert to the published version. The
// any-time counterpart to useAcceptWorkflowUpgrade (which only surfaces via the upgrade banner):
// clears just that pin, leaving the customer's wizard config untouched. Same invalidations.
export function useRemoveWorkflowCustomization(projectId: Ref<string>) {
    const queryClient = useQueryClient();
    const {mutate, mutateAsync, isPending, error, reset} = useMutation<unknown, ApiError, string>({
        mutationFn: (workflowId) =>
            fetchWrapper.delete(
                `${BASE}/${projectId.value}/workflows/${workflowId}/customization`
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["project", projectId.value]});
            queryClient.invalidateQueries({queryKey: ["project-version-diff", projectId.value]});
            queryClient.invalidateQueries({queryKey: ["project-wizard-steps", projectId.value]});
            queryClient.invalidateQueries({queryKey: ["project-workflows", projectId.value]});
        },
    });
    return {
        removeCustomization: mutate,
        removeCustomizationAsync: mutateAsync,
        removeCustomizationPending: isPending,
        removeCustomizationError: error as Ref<ApiError | null>,
        removeCustomizationReset: reset,
    };
}

// WRITE — save wizard sequence
export function useUpdateWizardSequence(projectId: Ref<string>) {
    const queryClient = useQueryClient();
    const {mutate, mutateAsync, isPending, error, reset} = useMutation<unknown, ApiError, string[]>({
        mutationFn: (workflowIds) =>
            fetchWrapper.put(`${BASE}/${projectId.value}/wizard-sequence`, workflowIds),
        onSuccess: () => queryClient.invalidateQueries({queryKey: ["wizard-sequence", projectId.value]}),
    });
    return {
        updateWizardSequence: mutate,
        updateWizardSequenceAsync: mutateAsync,
        updateWizardSequencePending: isPending,
        updateWizardSequenceError: error as Ref<ApiError | null>,
        updateWizardSequenceReset: reset,
    };
}

// WRITE — soft-delete (frees the name, moves the project to the recycle bin).
// Resolves on 200; rejects with an ApiError carrying `userMessage` on 409 when the backend blocks
// deletion (in-flight runs, a live published product, or active schedules). Callers should surface
// `deleteProjectError.userMessage`.
export function useDeleteProject() {
    const queryClient = useQueryClient();

    const {mutate, mutateAsync, isPending, error, reset} = useMutation<
        unknown,
        ApiError,
        string
    >({
        mutationFn: (id: string) => fetchWrapper.delete(`${BASE}/${id}`),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["projects"]});
            queryClient.invalidateQueries({queryKey: ["projects-deleted"]});
        },
    });

    return {
        deleteProject: mutate,
        deleteProjectAsync: mutateAsync,
        deleteProjectPending: isPending,
        deleteProjectError: error as Ref<ApiError | null>,
        deleteProjectReset: reset,
    };
}

// READ — soft-deleted projects (recycle bin), newest deletion first.
export function useDeletedProjects() {
    const {data, isFetching, error, refetch} = useQuery({
        queryKey: ["projects-deleted"],
        queryFn: () => fetchWrapper.get<Project[]>(`${BASE}/deleted`),
    });
    const deletedProjects = computed(() => data.value ?? []);

    return {
        deletedProjects,
        deletedProjectsLoading: isFetching,
        deletedProjectsError: error as Ref<ApiError | null>,
        refetchDeletedProjects: refetch,
    };
}

// WRITE — restore a soft-deleted project. Pass `newName` to rename when the original name was
// reused in the meantime; a 409 rejection means the chosen name is taken (caller prompts to rename).
export function useRestoreProject() {
    const queryClient = useQueryClient();

    const {mutate, mutateAsync, isPending, error, reset} = useMutation<
        unknown,
        ApiError,
        { id: string; newName?: string }
    >({
        mutationFn: ({id, newName}) => fetchWrapper.post(`${BASE}/${id}/restore`, {newName}),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["projects"]});
            queryClient.invalidateQueries({queryKey: ["projects-deleted"]});
        },
    });

    return {
        restoreProject: mutate,
        restoreProjectAsync: mutateAsync,
        restoreProjectPending: isPending,
        restoreProjectError: error as Ref<ApiError | null>,
        restoreProjectReset: reset,
    };
}

// WRITE — convert an existing project into a published product.
// CreateProduct atomically promotes the project to PublisherMaster and attaches the product;
// the only remaining client-side step is renaming the project to follow the "{name} Master"
// convention so the dashboard label matches the product.
export function useConvertProjectToProduct() {
    const queryClient = useQueryClient();

    const {mutate, mutateAsync, isPending, error, reset} = useMutation<
        Pick<Product, "id" | "name" | "description" | "masterProjectId">,
        ApiError,
        { projectId: string; name: string; description: string }
    >({
        mutationFn: async ({projectId, name, description}) => {
            const productId = await fetchWrapper.post<string>(PRODUCTS_BASE, {
                name,
                description,
                masterProjectId: projectId,
            });
            await fetchWrapper.put(`${BASE}/${projectId}`, {
                name: `${name} Master`,
            });
            return {id: productId, name, description, masterProjectId: projectId};
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["projects"]});
            queryClient.invalidateQueries({queryKey: ["products"]});
        },
    });

    return {
        convertProjectToProduct: mutate,
        convertProjectToProductAsync: mutateAsync,
        convertPending: isPending,
        convertError: error as Ref<ApiError | null>,
        convertReset: reset,
    };
}

// WRITE — create a product together with its master project in one operation
export function useCreateProductWithMasterProject() {
    const queryClient = useQueryClient();

    const {mutate, mutateAsync, isPending, error, reset} = useMutation<
        { product: Pick<Product, "id" | "name" | "description" | "masterProjectId">; project: Project },
        ApiError,
        { name: string; description: string }
    >({
        mutationFn: async ({name, description}) => {
            const projectId = await fetchWrapper.post<string>(BASE, {
                name: `${name} Master`,
                projectType: "PublisherMaster",
            });
            // CreateProduct now attaches the product to its master project transactionally,
            // so no follow-up PUT is required. The project is re-fetched only because callers
            // need the full Project shape (notably the assigned publisherProductId).
            const productId = await fetchWrapper.post<string>(PRODUCTS_BASE, {
                name,
                description,
                masterProjectId: projectId,
            });
            const project = await fetchWrapper.get<Project>(`${BASE}/${projectId}`);
            return {
                product: {id: productId, name, description, masterProjectId: projectId},
                project,
            };
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["projects"]});
            queryClient.invalidateQueries({queryKey: ["products"]});
        },
    });

    return {
        createProductWithMasterProject: mutate,
        createProductWithMasterProjectAsync: mutateAsync,
        createProductWithMasterPending: isPending,
        createProductWithMasterError: error as Ref<ApiError | null>,
        createProductWithMasterReset: reset,
    };
}

// UTILITY — invalidate from outside (e.g. after a project converts to a product)
export function useInvalidateProjects() {
    const queryClient = useQueryClient();
    return () => queryClient.invalidateQueries({queryKey: ["projects"]});
}
